import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "components/main/utils/Dialog";
import { Button } from "components/common/Button";
import { Stack, Inline } from "components/common/Stack";
import { TextInput } from "components/common/TextInput";
import { Spinner } from "components/common/Spinner";
import { DropdownMenu } from "components/common/DropdownMenu";
import { DropdownMenuItem } from "components/common/DropdownMenuItem";
import { Radio } from "components/common/Radio";
import { Callout } from "components/common/Callout";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import {
  useGooglePicker,
  type PickerResult,
} from "components/canvas/leftSide/import/GoogleSheets/useGooglePicker";
import { fetchSpreadsheet } from "components/canvas/leftSide/import/GoogleSheets/spreadsheetApi";
import { type SavedConnection } from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";
import { useExportToGoogleSheet } from "./useExportToGoogleSheet";
import {
  GOOGLE_SHEETS_MAX_CELLS,
  GOOGLE_SHEETS_SLOW_CELLS,
  type ExportSourceWire,
  type SheetExportDestination,
} from "./exportApi";

const accountLabel = (c: SavedConnection | null): string => {
  if (!c) return "";
  return c.credentials.type === "google_oauth"
    ? c.credentials.account_email
    : c.id;
};

/// Matches the `Select` field look so the account picker reads as a form input.
const FIELD_STYLE: React.CSSProperties = {
  height: "var(--control-height-md)",
  paddingInline: "var(--control-padding-x-md)",
  fontSize: "var(--font-size)",
  color: "var(--color-fg)",
  background: "var(--color-bg-subtle)",
  border: "var(--size-border-hairline) solid var(--color-border)",
  borderRadius: "var(--radius-app)",
  cursor: "pointer",
};

interface ExportToSheetsDialogProps {
  open: boolean;
  onClose: () => void;
  /** What to export, in the server's wire shape (table or view). */
  source: ExportSourceWire;
  /** Display name of the table/view — dialog header + default sheet name. */
  title: string;
  /**
   * Total cells (rows × columns) to export, pre-flighted against Sheets' cap.
   * Optional — when the caller can't cheaply know the column count (computed
   * tables in the overview), the server preflight remains the guard.
   */
  cellCount?: number;
}

type DestMode = "new" | "existing";
/// Within an existing spreadsheet: add a fresh tab, or replace one that's there.
type ExistingMode = "new_tab" | "overwrite_tab";

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const spreadsheetUrl = (id: string): string =>
  `https://docs.google.com/spreadsheets/d/${id}/edit`;

export const ExportToSheetsDialog: React.FC<ExportToSheetsDialogProps> = ({
  open,
  onClose,
  source,
  title,
  cellCount,
}) => {
  const {
    connections,
    connection,
    connectionHasSheets,
    selectConnection,
    connecting,
    connect,
    authorizeSheets,
    authorizing,
    needsReauth,
    flagReauthNeeded,
    phase,
    error,
    runExport,
    reset,
  } = useExportToGoogleSheet(source);

  // Rejected before any write (Sheets caps a spreadsheet at 10M cells). The
  // user can download Parquet/CSV instead.
  const tooLarge =
    cellCount !== undefined && cellCount > GOOGLE_SHEETS_MAX_CELLS;
  // Allowed, but slow enough to warn and suggest the faster CSV/Parquet path.
  const slow =
    !tooLarge &&
    cellCount !== undefined &&
    cellCount > GOOGLE_SHEETS_SLOW_CELLS;

  // Success is confirmed by the global toast — reset (so a later reopen starts
  // clean; the dialog stays mounted) and close once it lands.
  useEffect(() => {
    if (phase === "success") {
      reset();
      onClose();
    }
  }, [phase, reset, onClose]);
  const { openPicker, isPickerLoading } = useGooglePicker(connection);

  const [destMode, setDestMode] = useState<DestMode>("new");
  const [newTitle, setNewTitle] = useState(`${title} — ${todayIso()}`);
  const [picked, setPicked] = useState<PickerResult | null>(null);
  const [tabTitle, setTabTitle] = useState(title);
  const [existingMode, setExistingMode] = useState<ExistingMode>("new_tab");
  const [tabs, setTabs] = useState<string[]>([]);
  const [tabsLoading, setTabsLoading] = useState(false);
  const [tabsError, setTabsError] = useState<string | null>(null);
  const [overwriteTab, setOverwriteTab] = useState("");
  const [confirmingOverwrite, setConfirmingOverwrite] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [connectMsg, setConnectMsg] = useState<string | null>(null);
  const acctRef = useRef<HTMLButtonElement>(null);
  const [acctMenuOpen, setAcctMenuOpen] = useState(false);
  const tabRef = useRef<HTMLButtonElement>(null);
  const [tabMenuOpen, setTabMenuOpen] = useState(false);

  // A picked spreadsheet belongs to the account that picked it — switching
  // accounts must clear it so the user re-picks under the new account.
  useEffect(() => {
    setPicked(null);
  }, [connection?.id]);

  // Load the picked spreadsheet's existing tabs so overwrite targets a real tab
  // (no name typed by hand → no addSheet collision).
  useEffect(() => {
    if (!picked || !connection) {
      setTabs([]);
      return;
    }
    let cancelled = false;
    setTabsLoading(true);
    setTabsError(null);
    fetchSpreadsheet(spreadsheetUrl(picked.spreadsheetId), connection.id)
      .then((res) => {
        if (cancelled) return;
        const names = res.sheets.map((s) => s.name);
        setTabs(names);
        setOverwriteTab(names[0] ?? "");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTabs([]);
        setTabsError(
          e instanceof Error
            ? e.message
            : "Couldn't load the spreadsheet tabs.",
        );
      })
      .finally(() => {
        if (!cancelled) setTabsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [picked, connection]);

  const destination = useMemo<SheetExportDestination | null>(() => {
    if (destMode === "new") {
      return newTitle.trim()
        ? { kind: "new_spreadsheet", title: newTitle.trim() }
        : null;
    }
    if (!picked) return null;
    if (existingMode === "overwrite_tab") {
      return overwriteTab
        ? {
            kind: "existing_spreadsheet",
            spreadsheet_id: picked.spreadsheetId,
            target: { kind: "overwrite_tab", title: overwriteTab },
          }
        : null;
    }
    return tabTitle.trim()
      ? {
          kind: "existing_spreadsheet",
          spreadsheet_id: picked.spreadsheetId,
          target: { kind: "new_tab", title: tabTitle.trim() },
        }
      : null;
  }, [destMode, newTitle, picked, existingMode, overwriteTab, tabTitle]);

  const handleConnect = async () => {
    setConnectMsg(null);
    const outcome = await connect();
    if (outcome.status === "popup_blocked") {
      setConnectMsg("Popup blocked — allow popups and try again.");
    } else if (outcome.status === "error") {
      setConnectMsg(outcome.message);
    } else if (outcome.status === "email_mismatch") {
      setConnectMsg(
        `That account (${outcome.received}) doesn't match the expected ${outcome.expected}.`,
      );
    }
  };

  const handleAuthorizeSheets = async () => {
    setConnectMsg(null);
    setPickerError(null);
    const outcome = await authorizeSheets();
    if (outcome?.status === "popup_blocked") {
      setConnectMsg("Popup blocked — allow popups and try again.");
    } else if (outcome?.status === "error") {
      setConnectMsg(outcome.message);
    } else if (outcome?.status === "email_mismatch") {
      setConnectMsg(
        `That account (${outcome.received}) doesn't match the expected ${outcome.expected}.`,
      );
    }
  };

  const handlePick = async () => {
    setPickerError(null);
    const outcome = await openPicker();
    if (outcome.kind === "picked") {
      setPicked(outcome.result);
    } else if (
      outcome.kind === "needs-reauth" ||
      outcome.kind === "needs-sheets-reauth"
    ) {
      flagReauthNeeded();
    } else if (outcome.kind === "error") {
      setPickerError(outcome.message);
    }
  };

  const close = () => {
    setAcctMenuOpen(false);
    setConfirmingOverwrite(false);
    setPickerError(null);
    reset();
    onClose();
  };

  // Kick off the export; the dialog stays open showing progress (and any
  // error, so a reauthorize can be retried in place). It closes itself on
  // success via the effect above.
  const startExport = (dest: SheetExportDestination) => {
    void runExport(dest);
  };

  // Overwriting a tab destroys its contents — gate it behind one confirm step.
  // Everything else exports straight away.
  const handleExportClick = () => {
    if (!destination) return;
    if (
      destination.kind === "existing_spreadsheet" &&
      destination.target.kind === "overwrite_tab"
    ) {
      setConfirmingOverwrite(true);
      return;
    }
    startExport(destination);
  };

  // ---- body by phase ----------------------------------------------------

  let body: React.ReactNode;
  let actions: React.ReactNode;

  if (phase === "exporting" || phase === "success") {
    // "success" shows the same bar for the one frame before the effect closes
    // the dialog — avoids a flash of stale content.
    body = (
      <Stack gap="sm">
        <span className="text-fg-muted-regular">Writing to Google Sheets…</span>
        <div
          className="indicator-progress"
          style={{ color: "var(--color-accent)" }}
        >
          <div className="indicator-progress-bar-indeterminate" />
        </div>
      </Stack>
    );
    actions = null;
  } else if (!connection) {
    body = (
      <Stack gap="md">
        <span className="text-fg-muted-regular">
          Connect a Google account to let MyCorr write a spreadsheet for you. We
          only ever touch sheets you create here or pick yourself.
        </span>
        {connectMsg && <span className="text-error-regular">{connectMsg}</span>}
      </Stack>
    );
    actions = (
      <Button
        variant="primary"
        size="md"
        loading={connecting}
        onClick={handleConnect}
      >
        Connect Google account
      </Button>
    );
  } else if (needsReauth) {
    body = (
      <Stack gap="sm">
        <span className="text-fg-regular">
          Google Sheets access for <strong>{accountLabel(connection)}</strong>{" "}
          has expired.
        </span>
        <span className="text-fg-muted-regular">
          Reauthorize to keep exporting. You can also do this anytime from the
          Connections page.
        </span>
        {connectMsg && <span className="text-error-regular">{connectMsg}</span>}
      </Stack>
    );
    actions = (
      <Button
        variant="primary"
        size="md"
        loading={authorizing}
        onClick={handleAuthorizeSheets}
      >
        Reauthorize Google Sheets
      </Button>
    );
  } else if (confirmingOverwrite) {
    body = (
      <Stack gap="sm">
        <span className="text-fg-regular">
          Replace everything in the <strong>{overwriteTab}</strong> tab of{" "}
          <strong>{picked?.name}</strong> with “{title}”?
        </span>
        <span className="text-fg-muted-regular">
          The tab’s current contents will be overwritten. This can’t be undone.
        </span>
        {error && <span className="text-error-regular">{error}</span>}
      </Stack>
    );
    actions = (
      <>
        <Button
          variant="secondary"
          size="md"
          onClick={() => setConfirmingOverwrite(false)}
        >
          Back
        </Button>
        <Button
          variant="destructive"
          size="md"
          onClick={() => destination && startExport(destination)}
        >
          Replace contents
        </Button>
      </>
    );
  } else {
    // destination picker (covers phase "idle" and "error")
    body = (
      <Stack gap="lg">
        {tooLarge && (
          <Callout variant="error">
            This table has {(cellCount ?? 0).toLocaleString()} cells, over
            Google Sheets’ {GOOGLE_SHEETS_MAX_CELLS.toLocaleString()}-cell
            limit. Download it as Parquet or CSV instead.
          </Callout>
        )}
        {slow && (
          <Callout variant="warning">
            Large export ({(cellCount ?? 0).toLocaleString()} cells) — writing
            to Google Sheets may take a minute or two. A CSV or Parquet download
            is faster.
          </Callout>
        )}
        {/* Google account — pick a pre-existing connection or add a new one */}
        <Stack gap="2xs">
          <span className="text-fg-muted-semibold item-height flex items-center">
            Google account
          </span>
          <button
            ref={acctRef}
            type="button"
            onClick={() => setAcctMenuOpen((o) => !o)}
            className="flex items-center justify-between w-full gap-2"
            style={FIELD_STYLE}
          >
            <span className="truncate">{accountLabel(connection)}</span>
            <ExpandMoreOutlinedIcon
              fontSize="small"
              style={{ color: "var(--color-fg-muted)" }}
            />
          </button>
          <DropdownMenu
            anchorRef={acctRef}
            isOpen={acctMenuOpen}
            onClose={() => setAcctMenuOpen(false)}
            placement="bottom"
          >
            {connections.map((c) => (
              <DropdownMenuItem
                key={c.id}
                isSelected={c.id === connection.id}
                className="px-3"
                onClick={() => {
                  selectConnection(c.id);
                  setAcctMenuOpen(false);
                }}
              >
                {accountLabel(c)}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem
              className="px-3 font-medium"
              onClick={() => {
                setAcctMenuOpen(false);
                void handleConnect();
              }}
            >
              + Connect another account
            </DropdownMenuItem>
          </DropdownMenu>
          {connectMsg && (
            <span className="text-error-regular">{connectMsg}</span>
          )}
        </Stack>

        {connectionHasSheets ? (
          <Stack gap="md">
            <span className="text-fg-muted-regular">Choose how to export</span>

            {/* Way 1 — a brand-new spreadsheet */}
            <div
              className={`rounded-app border p-4 ${
                destMode === "new"
                  ? "border-accent bg-accent/10"
                  : "border-border"
              }`}
            >
              <button
                type="button"
                onClick={() => setDestMode("new")}
                className="w-full flex justify-between items-center gap-3 bg-transparent border-0 p-0 text-left cursor-pointer"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="text-fg-medium">New spreadsheet</span>
                  <span className="text-fg-muted-regular">
                    Create a brand-new Google Sheet.
                  </span>
                </div>
                <Radio selected={destMode === "new"} />
              </button>
              {destMode === "new" && (
                <div className="mt-3">
                  <TextInput
                    value={newTitle}
                    onChange={setNewTitle}
                    placeholder="Spreadsheet name"
                  />
                </div>
              )}
            </div>

            {/* Way 2 — a tab in a spreadsheet you pick */}
            <div
              className={`rounded-app border p-4 ${
                destMode === "existing"
                  ? "border-accent bg-accent/10"
                  : "border-border"
              }`}
            >
              <button
                type="button"
                onClick={() => setDestMode("existing")}
                className="w-full flex justify-between items-center gap-3 bg-transparent border-0 p-0 text-left cursor-pointer"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="text-fg-medium">Existing spreadsheet</span>
                  <span className="text-fg-muted-regular">
                    Add a new tab — or overwrite one — in a sheet you pick from
                    Drive.
                  </span>
                </div>
                <Radio selected={destMode === "existing"} />
              </button>
              {destMode === "existing" && (
                <Stack gap="sm" className="mt-3">
                  <button
                    type="button"
                    onClick={handlePick}
                    disabled={isPickerLoading}
                    className="flex items-center justify-between w-full gap-2"
                    style={FIELD_STYLE}
                  >
                    <span
                      className={`truncate ${picked ? "" : "text-fg-muted-regular"}`}
                    >
                      {isPickerLoading
                        ? "Opening Google Drive…"
                        : picked
                          ? picked.name
                          : "Choose from Google Drive…"}
                    </span>
                    <FolderOpenOutlinedIcon
                      style={{
                        color: "var(--color-fg-muted)",
                        fontSize: "1rem",
                      }}
                    />
                  </button>
                  {pickerError && (
                    <span className="text-error-regular">{pickerError}</span>
                  )}
                  {picked && (
                    <Stack gap="sm">
                      <Inline gap="md">
                        <button
                          type="button"
                          onClick={() => setExistingMode("new_tab")}
                          className="flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer"
                        >
                          <Radio selected={existingMode === "new_tab"} />
                          <span className="text-fg-regular">New tab</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExistingMode("overwrite_tab")}
                          disabled={tabs.length === 0 && !tabsLoading}
                          className="flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50"
                        >
                          <Radio selected={existingMode === "overwrite_tab"} />
                          <span className="text-fg-regular">
                            Overwrite a tab
                          </span>
                        </button>
                      </Inline>

                      {tabsError && (
                        <span className="text-error-regular">{tabsError}</span>
                      )}

                      {existingMode === "new_tab" ? (
                        <TextInput
                          value={tabTitle}
                          onChange={setTabTitle}
                          placeholder="New tab name"
                        />
                      ) : tabsLoading ? (
                        <div
                          className="flex items-center gap-2 w-full"
                          style={{ ...FIELD_STYLE, cursor: "default" }}
                        >
                          <Spinner />
                          <span className="text-fg-muted-regular">
                            Loading tabs…
                          </span>
                        </div>
                      ) : (
                        tabs.length > 0 && (
                          <>
                            <button
                              ref={tabRef}
                              type="button"
                              onClick={() => setTabMenuOpen((o) => !o)}
                              className="flex items-center justify-between w-full gap-2"
                              style={FIELD_STYLE}
                            >
                              <span className="truncate">{overwriteTab}</span>
                              <ExpandMoreOutlinedIcon
                                fontSize="small"
                                style={{ color: "var(--color-fg-muted)" }}
                              />
                            </button>
                            <DropdownMenu
                              anchorRef={tabRef}
                              isOpen={tabMenuOpen}
                              onClose={() => setTabMenuOpen(false)}
                              placement="bottom"
                            >
                              {tabs.map((t) => (
                                <DropdownMenuItem
                                  key={t}
                                  isSelected={t === overwriteTab}
                                  onClick={() => {
                                    setOverwriteTab(t);
                                    setTabMenuOpen(false);
                                  }}
                                >
                                  {t}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenu>
                          </>
                        )
                      )}
                    </Stack>
                  )}
                </Stack>
              )}
            </div>
          </Stack>
        ) : (
          <Stack gap="sm">
            <span className="text-fg-muted-regular">
              This account hasn’t granted Google Sheets access yet. Authorize it
              to let MyCorr write spreadsheets — the same read &amp; write
              permission Sheets import uses.
            </span>
            <Button
              variant="primary"
              size="sm"
              loading={authorizing}
              onClick={handleAuthorizeSheets}
              className="self-start"
            >
              Authorize Google Sheets
            </Button>
          </Stack>
        )}

        {error && <span className="text-error-regular">{error}</span>}
      </Stack>
    );
    actions = (
      <Button
        variant="primary"
        size="md"
        disabled={!connectionHasSheets || !destination || tooLarge}
        onClick={handleExportClick}
      >
        Export
      </Button>
    );
  }

  return (
    <Dialog
      open={open}
      title={`Export “${title}” to Google Sheets`}
      onClose={close}
      actions={actions}
      style={{ width: "32rem", maxWidth: "95vw" }}
    >
      {body}
    </Dialog>
  );
};
